/**
 * `<CompendiumExplorer>` — a lighter, draggable + resizable floating panel for browsing,
 * reading, and picking ONE compendium item (a talent or a skill trigger) to add to a pilot:
 * a search → read-the-detail → select experience.
 *
 * **Context-blind.** The host (the sheets' EDIT mode, `MechSheetEdit.tsx` / `PilotSheetEdit.tsx`) maps its compendium items into
 * flat {@link ExplorerEntry}s (name + meta + terse + HTML body sections), so the explorer
 * stays agnostic to the talent-vs-skill shape and owns only the chrome: search filter, the
 * scrolling result list, the read pane, and the ADD action. Selecting calls `onSelect(id)`
 * (the host dispatches the real `RosterCommand`) and the host closes the panel.
 *
 * **Lighter floating panel**, not the full GMS window: a self-contained `position: fixed`
 * card dragged by its header and resized from the bottom-right grip (document-level mouse
 * listeners so a fast drag that outruns the handle still tracks). Visuals reuse the
 * brutalist editor language (`brutalist-editor.css`, the `.be-exp*` rules).
 *
 * The built-in read pane's `sections` render compendium HTML (`<br>`, `<ul>`, `<li>`, `<b>` …)
 * through the shared {@link RulesText}, which **parses the fragment to elements rather than
 * injecting it** — never reach for `dangerouslySetInnerHTML` here: injected, a `<br>` renders
 * exactly like a wrapped line, so a seven-paragraph talent rank arrives with no visible
 * paragraph boundaries at all. **Note this path is currently unreached:** every host (both
 * editors, all eight pickers) passes `renderDetail`, so the rich `CompendiumRecordReader` owns
 * the pane and `sections` goes unused.
 */
import { faLock, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { tipAttrs } from "../tooltip";
import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from "react";
import { RulesText } from "../RulesText";
import "../../routes/home/brutalist-editor.css";

/** A flat, presentation-ready item for the explorer — the host maps compendium items to these. */
export type ExplorerEntry = {
  id: string;
  name: string;
  /** A short tag shown beside the name (e.g. "TALENT"). */
  meta?: string;
  /** A one-line summary shown under the name in the list and atop the built-in read pane. */
  terse?: string;
  /**
   * The body, as titled HTML blocks (talent ranks, a skill's long detail, …) — drives the
   * built-in read pane. Optional, and ignored entirely when the host supplies `renderDetail`
   * (which owns the read pane), in which case the entry need only carry `id`/`name`/`meta`.
   */
  sections?: { heading?: string; html: string }[];
  /**
   * When set, this entry can't be added (e.g. a license-locked item) — `reason` says why
   * (e.g. "Requires Blackbeard II"). The explorer still lists it (greyed, sorted after the
   * available items, readable in the detail pane) but disables the ADD action; the picker
   * shows what could be unlocked without letting the reducer wall reject it on click.
   */
  locked?: { reason: string };
};

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * A press-drag gesture. `onDelta(dx, dy)` fires with the cursor offset from the press
 * origin; the caller clamps and applies it against a base captured at press time. Mirrors
 * `WindowFrame`'s gesture, kept local so the kit doesn't depend on COMBAT.
 */
function useDragGesture(onDelta: (dx: number, dy: number) => void) {
  const [active, setActive] = useState(false);
  const start = useRef({ x: 0, y: 0 });
  useEffect(() => {
    if (!active) return;
    const onMove = (e: MouseEvent) => onDelta(e.clientX - start.current.x, e.clientY - start.current.y);
    const onUp = () => setActive(false);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
  }, [active, onDelta]);
  return (e: ReactMouseEvent) => {
    start.current = { x: e.clientX, y: e.clientY };
    setActive(true);
    e.preventDefault();
  };
}

export function CompendiumExplorer({
  title,
  accent = false,
  entries,
  onSelect,
  onClose,
  renderDetail,
}: {
  /** The panel title, e.g. "ADD TALENT". */
  title: string;
  /** Tint the ADD button / active row with the editor accent (talents read accented). */
  accent?: boolean;
  entries: ExplorerEntry[];
  onSelect: (id: string) => void;
  onClose: () => void;
  /**
   * Optional read-pane renderer for the selected entry. When supplied, it OWNS the read pane —
   * the host renders whatever rich detail it likes (e.g. the COMPENDIUM record sheet) and the
   * explorer drops its built-in `terse`/`sections` view. The explorer stays context-blind: it
   * passes only the selected id back out. When omitted, the built-in HTML-sections view is used.
   */
  renderDetail?: (id: string) => ReactNode;
}) {
  const [query, setQuery] = useState("");
  // Default to the first AVAILABLE entry (not a locked one), so the panel opens on something the
  // user can actually add rather than a greyed, disabled-ADD item.
  const [selectedId, setSelectedId] = useState<string | null>(
    (entries.find((e) => !e.locked) ?? entries[0])?.id ?? null,
  );

  // Position + size of the floating card. Centred on first mount against the viewport.
  const [pos, setPos] = useState(() => ({
    x: Math.max(24, Math.round(window.innerWidth / 2 - 430)),
    y: Math.max(24, Math.round(window.innerHeight / 2 - 310)),
  }));
  const [size, setSize] = useState({ w: 860, h: 620 });
  const posBase = useRef({ x: 0, y: 0 });
  const sizeBase = useRef({ w: 0, h: 0 });

  const onHeaderDown = useDragGesture((dx, dy) =>
    setPos({
      x: clamp(posBase.current.x + dx, 0, window.innerWidth - 120),
      y: clamp(posBase.current.y + dy, 0, window.innerHeight - 60),
    }),
  );
  const beginDrag = (e: ReactMouseEvent) => {
    posBase.current = { x: pos.x, y: pos.y };
    onHeaderDown(e);
  };
  const onGripDown = useDragGesture((dx, dy) =>
    setSize({
      w: clamp(sizeBase.current.w + dx, 360, window.innerWidth - 40),
      h: clamp(sizeBase.current.h + dy, 260, window.innerHeight - 40),
    }),
  );
  const beginResize = (e: ReactMouseEvent) => {
    sizeBase.current = { w: size.w, h: size.h };
    onGripDown(e);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched =
      q === ""
        ? entries
        : entries.filter((e) => {
            const hay = `${e.name} ${e.meta ?? ""} ${e.terse ?? ""} ${(e.sections ?? []).map((s) => `${s.heading ?? ""} ${s.html}`).join(" ")}`;
            return hay.toLowerCase().includes(q);
          });
    // Available items first, locked (e.g. license-locked) ones after — a stable sort keeps
    // the host's alphabetical order within each group.
    return [...matched].sort((a, b) => Number(Boolean(a.locked)) - Number(Boolean(b.locked)));
  }, [entries, query]);

  // Keep the selection valid as the filter narrows: if the selected entry drops out, fall
  // back to the first match.
  const selected = filtered.find((e) => e.id === selectedId) ?? filtered[0] ?? null;

  // Close on Escape, like a dialog.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="be-doc be-exp"
      role="dialog"
      aria-label={title}
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h }}
    >
      <div className="be-exp-header" onMouseDown={beginDrag}>
        <span className="be-exp-title">{title}</span>
        <button type="button" className="be-exp-close" aria-label="Close explorer" {...tipAttrs({ label: "Close explorer" })} onClick={onClose}>
          <Icon icon={faXmark} />
        </button>
      </div>

      <input
        className="be-exp-search"
        autoFocus
        value={query}
        placeholder="SEARCH…"
        aria-label={`Search ${title}`}
        onChange={(e) => setQuery(e.target.value)}
      />

      <div className="be-exp-body">
        <div className="be-exp-list">
          {filtered.length === 0 && <div className="be-empty" style={{ padding: "10px 12px" }}>— no matches —</div>}
          {filtered.map((e) => (
            <button
              type="button"
              key={e.id}
              className={`be-exp-item${selected?.id === e.id ? " active" : ""}${e.locked ? " locked" : ""}`}
              onClick={() => setSelectedId(e.id)}
            >
              <span className="be-exp-itemname">
                {e.locked && <span className="be-exp-lock" aria-hidden="true"><Icon icon={faLock} /> </span>}
                {e.name}
              </span>
              {(e.locked ?? e.meta) && (
                <span className="be-exp-itemmeta">{e.locked ? e.locked.reason : e.meta}</span>
              )}
            </button>
          ))}
        </div>

        <div className={`be-exp-read${renderDetail ? " rich" : ""}`}>
          {selected ? (
            renderDetail ? (
              renderDetail(selected.id)
            ) : (
              <>
                <div className="be-exp-readhead">
                  <div className="be-exp-readname">{selected.name}</div>
                  {selected.meta && <div className="be-exp-readmeta">{selected.meta}</div>}
                </div>
                {selected.terse && <p className="be-exp-terse">{selected.terse}</p>}
                {(selected.sections ?? []).map((s, i) => (
                  <div key={i} className="be-exp-section">
                    {s.heading && <div className="be-exp-sechead">{s.heading}</div>}
                    <RulesText className="be-exp-prose" html={s.html} />
                  </div>
                ))}
              </>
            )
          ) : (
            <div className="be-empty" style={{ padding: "10px 12px" }}>— nothing to read —</div>
          )}
        </div>
      </div>

      <div className="be-exp-footer">
        <button
          type="button"
          className={`be-btn be-btn-primary${accent ? " be-exp-add-accent" : ""}`}
          disabled={!selected || Boolean(selected.locked)}
          title={selected?.locked ? selected.locked.reason : undefined}
          onClick={() => selected && !selected.locked && onSelect(selected.id)}
        >
          {selected?.locked ? (
            <>
              <Icon icon={faLock} className="icon-lead" />{selected.locked.reason.toUpperCase()}
            </>
          ) : (
            <>
              <Icon icon={faPlus} className="icon-lead" />ADD {selected ? selected.name.toUpperCase() : ""}
            </>
          )}
        </button>
      </div>

      <div className="be-exp-grip" onMouseDown={beginResize} aria-hidden="true" />
    </div>
  );
}
